/**
 * The projection engine.
 *
 * Ported verbatim from vudget-dashboard.html so behaviour is identical. Runs in
 * the browser on every slider drag, and in the Worker for /api/dashboard.
 *
 * Two ideas carry most of the weight:
 *
 *  1. INTENT vs EFFECTIVE dials. `scenario.dials` stores what you asked for.
 *     `rebalance()` derives the effective value by restarting from intent every
 *     time and only then trimming. That's why freeing up room automatically
 *     gives it back — restoration isn't a separate code path that can drift.
 *
 *  2. Everything is recomputed from the baseline each month. No incremental
 *     state to get out of sync.
 */

import type {
  CategoryItem, CspBucket, CspGroup, DerivedCategory, Impact, IncomeAssumptions,
  NetWorthSnapshot, PriorityEntry, ProjectionResult, ProjectionRow, Scenario, ScenarioEvent,
} from './types.js';
import type { NetBasis } from './calendar.js';
import { DEFAULT_CUT_ORDER, GROUP_TUNING, cutPriorityFor } from './groups.js';
import { totalNetWorth } from './finance.js';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export interface ProjectionInput {
  cats: DerivedCategory[];
  /** effective dials — run rebalance() first */
  dials: Record<string, number>;
  events: ScenarioEvent[];
  income: IncomeAssumptions;
  months: number;
  marketReturnPct: number;
  netMonthlyIncome: number;
  grossMonthlyIncome: number;
  /**
   * Net pay per month index, when the real calendar is known.
   *
   * `netMonthlyIncome` is `annual / 12` — a salary nobody receives. On a
   * bi-weekly schedule ten months deliver two paychecks and two deliver three,
   * so ten months a year run short of the twelfth and two run long. Smoothing
   * that away is what let a plan look solvent through a stretch it would not
   * survive: `buffer` and `brokeAt` never saw the shortfall.
   *
   * Absent (or on the accrual basis) the flat figure is used, which is still
   * the right model for long-horizon compounding.
   */
  netByMonth?: (monthIndex: number) => number;
  latest: NetWorthSnapshot;
  /** month 0 of the projection */
  startYear: number;
  startMonth: number;
  /** ignore dials entirely — used for the "if nothing changed" baseline */
  ignoreDials?: boolean;
}

export function monthLabel(startYear: number, startMonth: number, i: number): string {
  const t = startMonth + i;
  const yr = startYear + Math.floor(t / 12);
  return `${MONTH_NAMES[((t % 12) + 12) % 12]} '${String(yr).slice(2)}`;
}

/**
 * What this item's dial is set to, or its natural default when untouched —
 * 100% for an active item, 0% for one that's only in the list because a
 * caller asked for paused/inactive rows too. Falls back to 100 when the item
 * itself doesn't carry a default (e.g. legacy callers passing a bare id).
 */
export const dialOf = (
  dials: Record<string, number>,
  item: { id: string; defaultDialPct?: number },
): number =>
  dials[item.id] ?? item.defaultDialPct ?? 100;

/**
 * What 100% of a line means, on the axis being reasoned with.
 *
 * A dial is a percentage, and a percentage is axis-invariant — it is the *base*
 * it multiplies that carries the axis. Threading the basis here rather than
 * scaling a total afterwards is what keeps a mixed-cadence category correct: a
 * group holding an HSA line (24 checks) beside everything else (26) converts
 * per line and sums two right answers, instead of applying one wrong factor to
 * the basket. That single global factor is the exact bug `shared/calendar.ts`
 * was written to kill, and re-introducing it one layer up would be no better.
 *
 * `guaranteedBase` is populated by `deriveCategories`; a hand-built item from an
 * older fixture may not carry it, so `base` is the fallback rather than zero.
 */
export const baseOf = (
  item: Pick<CategoryItem, 'base'> & Partial<Pick<CategoryItem, 'guaranteedBase'>>,
  basis: NetBasis = 'accrual',
): number => (basis === 'guaranteed' ? item.guaranteedBase ?? item.base : item.base);

/** Un-dialled cost of a whole category on the given axis. */
export const catBase = (cat: DerivedCategory, basis: NetBasis = 'accrual'): number =>
  basis === 'guaranteed' ? cat.guaranteedBase ?? cat.base : cat.base;

/** Current value of a category under a given set of dials. */
export function catValue(
  cat: DerivedCategory,
  dials: Record<string, number>,
  basis: NetBasis = 'accrual',
): number {
  return cat.items.reduce((sum, i) => sum + baseOf(i, basis) * (dialOf(dials, i) / 100), 0);
}

/**
 * A category's applied percentage.
 *
 * Numerator and denominator are read on the *same* axis deliberately. Mixing
 * them — an accrual numerator over a guaranteed denominator — is what made the
 * budget meter jump 7.1 points on a basis flip with no plan change, and it is
 * the single failure mode this whole change set exists to remove.
 */
export function catPct(
  cat: DerivedCategory,
  dials: Record<string, number>,
  basis: NetBasis = 'accrual',
): number {
  const b = catBase(cat, basis);
  return b > 0 ? (catValue(cat, dials, basis) / b) * 100 : 100;
}

export const catsTotal = (
  cats: DerivedCategory[],
  dials: Record<string, number>,
  basis: NetBasis = 'accrual',
): number => cats.reduce((sum, c) => sum + catValue(c, dials, basis), 0);

/**
 * The total that has to fit inside take-home.
 *
 * Payroll deductions (401K, HSA) are excluded: they leave gross before the
 * paycheck lands, so counting them here made the plan look over-committed by
 * their combined size and dragged auto-balance into cutting real spending to
 * cover money that was never in the account.
 *
 * The basis must match whichever income figure this is being measured against.
 * Pairing this total with `netGuaranteedMonthly` while leaving it on accrual is
 * precisely the axis-mixing the basis parameter exists to prevent.
 */
export const takeHomeTotal = (
  cats: DerivedCategory[],
  dials: Record<string, number>,
  basis: NetBasis = 'accrual',
): number =>
  cats.reduce((sum, c) => sum + (c.deduction === null ? catValue(c, dials, basis) : 0), 0);

/**
 * What the payroll deductions (401K, HSA, …) currently draw under a set of dials.
 *
 * The mirror image of `takeHomeTotal`: everything with a `deduction` and nothing
 * without one. Used to work out how much a dialled-up deduction has shrunk the
 * paycheck.
 */
export const deductionTotal = (
  cats: DerivedCategory[],
  dials: Record<string, number>,
  basis: NetBasis = 'accrual',
): number =>
  cats.reduce((sum, c) => sum + (c.deduction !== null ? catValue(c, dials, basis) : 0), 0);

/**
 * The un-dialled cost of the payroll deductions — the baseline Notion's Net
 * figure has already removed.
 */
export const deductionBaseTotal = (
  cats: DerivedCategory[],
  basis: NetBasis = 'accrual',
): number =>
  cats.reduce((sum, c) => sum + (c.deduction !== null ? catBase(c, basis) : 0), 0);

/**
 * Take-home income after the deduction dials have had their say.
 *
 * `baseIncome` (Notion's Net) already has the *base* 401K/HSA contributions
 * removed, so only the *delta* from base moves take-home: dial a deduction above
 * base and the paycheck shrinks; below base and it grows. This is the same
 * adjustment `runProjection` makes to `monthIncome`
 * (`landed − (deductionTotal − deductionBase)`) — pulling it out here lets the
 * budget meter and the cap measure against the paycheck a plan actually lands,
 * instead of a static figure that ignored every deduction dial.
 *
 * Deductions are never trimmed by the cap (`rebalance` skips them), so their
 * effective value always equals their intent — which is why this can be computed
 * from intent dials before `rebalance` runs, with no circular dependency.
 */
export const takeHomeIncome = (
  baseIncome: number,
  cats: DerivedCategory[],
  dials: Record<string, number>,
  basis: NetBasis = 'accrual',
): number =>
  baseIncome - (deductionTotal(cats, dials, basis) - deductionBaseTotal(cats, basis));

/** Floor % below which auto-balance won't cut a category. */
export function floorOf(cat: DerivedCategory, priority: PriorityEntry[]): number {
  const entry = priority.find(p => p.groupKey === cat.key);
  return entry?.floorPct ?? cat.minPct ?? 0;
}

export function isLocked(key: string, priority: PriorityEntry[]): boolean {
  return priority.find(p => p.groupKey === key)?.locked ?? false;
}

export function cutOrder(priority: PriorityEntry[]): string[] {
  if (!priority.length) return DEFAULT_CUT_ORDER.slice();
  return priority.slice().sort((a, b) => a.sortOrder - b.sortOrder).map(p => p.groupKey);
}

/**
 * Rebuild effective dials from intent, then trim by priority if the cap is on
 * and we're over income.
 *
 * Restarting from intent is the whole trick — it makes restoration automatic
 * rather than a mirror-image code path that could drift from the cutting logic.
 */
export function rebalance(
  cats: DerivedCategory[],
  intent: Record<string, number>,
  opts: {
    capAtIncome: boolean; income: number; priority: PriorityEntry[];
    protectKey?: string | null;
    /** Which axis `income` is on. The totals below are read on the same one. */
    basis?: NetBasis;
  },
): Record<string, number> {
  const basis = opts.basis ?? 'accrual';
  const effective: Record<string, number> = {};
  for (const c of cats) for (const i of c.items) effective[i.id] = dialOf(intent, i);

  if (!opts.capAtIncome) return effective;

  let total = takeHomeTotal(cats, effective, basis);
  if (total <= opts.income + 0.01) return effective;

  for (const key of cutOrder(opts.priority)) {
    if (total <= opts.income + 0.01) break;
    if (isLocked(key, opts.priority) || key === opts.protectKey) continue;
    const cat = cats.find(c => c.key === key);
    if (!cat) continue;
    // Cutting a payroll deduction can't free up take-home — it isn't spending
    // take-home in the first place.
    if (cat.deduction !== null) continue;

    // Scoped to active items only — a paused item riding along in the same
    // CSP group isn't part of the plan the cap is protecting, and writing a
    // uniform cut % across the whole group would otherwise reactivate it as a
    // side effect of trimming something else.
    const active = cat.items.filter(i => i.status === 'A');
    const activeBase = active.reduce((s, i) => s + baseOf(i, basis), 0);
    if (activeBase <= 0) continue;
    const current = active.reduce((s, i) => s + baseOf(i, basis) * (dialOf(effective, i) / 100), 0);
    const floor = activeBase * (floorOf(cat, opts.priority) / 100);
    const room = current - floor;
    if (room <= 0.5) continue;

    const cut = Math.min(room, total - opts.income);
    const newPct = ((current - cut) / activeBase) * 100;
    for (const i of active) effective[i.id] = Number(newPct.toFixed(1));
    total -= cut;
  }
  return effective;
}

export type BalanceMode = 'priority' | 'proportional';

/**
 * One-shot rebalance that bakes the result in as the new intent.
 * (The cap toggle is a live constraint; this button is a commit.)
 *
 * Two strategies, because pure priority order is brutal in practice — it drains
 * Guilt-Free to zero before touching Subscriptions at all:
 *
 *  - `proportional` (default): every unlocked category gives up the same
 *    *fraction of its headroom above floor*. The pain is shared, nothing gets
 *    obliterated, and priority still matters because it weights the shares.
 *  - `priority`: strict cascade, cheapest-to-lose first. Use when you genuinely
 *    want Guilt-Free gone before Groceries moves at all.
 */
export function autoBalance(
  cats: DerivedCategory[],
  intent: Record<string, number>,
  opts: { income: number; priority: PriorityEntry[]; mode?: BalanceMode; basis?: NetBasis },
): Record<string, number> {
  const basis = opts.basis ?? 'accrual';
  const next = { ...intent };
  const total = takeHomeTotal(cats, next, basis);
  let excess = total - opts.income;
  if (excess <= 0.01) return next;

  const order = cutOrder(opts.priority);

  // Same reasoning as rebalance()'s cut loop: a paused item along for the ride
  // in a CSP group isn't part of what auto-balance is trimming, so both the
  // headroom math and the uniform pct it writes back are scoped to active
  // items only.
  const activeItems = (cat: DerivedCategory) => cat.items.filter(i => i.status === 'A');
  const activeBase = (cat: DerivedCategory) =>
    activeItems(cat).reduce((s, i) => s + baseOf(i, basis), 0);
  const activeValue = (cat: DerivedCategory, dials: Record<string, number>) =>
    activeItems(cat).reduce((s, i) => s + baseOf(i, basis) * (dialOf(dials, i) / 100), 0);

  /** Headroom each category can give up, in dollars. */
  const room = (cat: DerivedCategory, dials: Record<string, number>) =>
    Math.max(0, activeValue(cat, dials) - activeBase(cat) * (floorOf(cat, opts.priority) / 100));

  const setTo = (cat: DerivedCategory, dollars: number) => {
    const base = activeBase(cat);
    const pct = base > 0 ? (dollars / base) * 100 : 0;
    for (const i of activeItems(cat)) next[i.id] = Number(pct.toFixed(1));
  };

  const eligible = order
    .map(k => cats.find(c => c.key === k))
    .filter((c): c is DerivedCategory =>
      !!c && c.deduction === null && !isLocked(c.key, opts.priority));

  if ((opts.mode ?? 'proportional') === 'priority') {
    for (const cat of eligible) {
      if (excess <= 0.01) break;
      const r = room(cat, next);
      if (r <= 0.5) continue;
      const cut = Math.min(r, excess);
      setTo(cat, activeValue(cat, next) - cut);
      excess -= cut;
    }
    return next;
  }

  // Proportional: weight by priority position so earlier entries still give up
  // more, but everyone contributes. Repeat until satisfied or everything floors.
  for (let pass = 0; pass < 8 && excess > 0.01; pass++) {
    const live = eligible.filter(c => room(c, next) > 0.5);
    if (!live.length) break;

    const weights = live.map(c => {
      const rank = eligible.indexOf(c);
      // linear decay: first in the cut order carries ~2x the weight of the last
      const w = 2 - rank / Math.max(1, eligible.length - 1);
      return { cat: c, weight: w * room(c, next) };
    });
    const totalWeight = weights.reduce((s, w) => s + w.weight, 0);
    if (totalWeight <= 0) break;

    let applied = 0;
    for (const { cat, weight } of weights) {
      const share = (weight / totalWeight) * excess;
      const cut = Math.min(room(cat, next), share);
      if (cut <= 0) continue;
      setTo(cat, activeValue(cat, next) - cut);
      applied += cut;
    }
    if (applied <= 0.01) break;
    excess -= applied;
  }

  return next;
}

export function bonusGross(income: IncomeAssumptions, grossAnnual: number): number {
  return income.bonusMode === 'pct'
    ? grossAnnual * (income.bonusPct / 100)
    : income.bonusAnnual;
}

/** Month-by-month projection. */
export function runProjection(input: ProjectionInput): ProjectionResult {
  const {
    cats, dials, events, income, months, marketReturnPct,
    netMonthlyIncome, grossMonthlyIncome, latest, startYear, startMonth, ignoreDials,
    netByMonth,
  } = input;

  const rows: ProjectionRow[] = [];
  const grossAnnual = grossMonthlyIncome * 12;
  const bGross = bonusGross(income, grossAnnual);

  let cash = 0;
  let netWorth = totalNetWorth(latest);
  let investBalance = latest.investments;
  let brokeAt: number | null = null;
  const ef0 = latest.emergencyFund;

  for (let m = 0; m < months; m++) {
    const yr = Math.floor(m / 12);
    const inflationFactor = income.applyInflation
      ? Math.pow(1 + income.inflationPct / 100, yr)
      : 1;

    const byCategory: Record<string, number> = {};
    const byBucket: Record<CspBucket, number> = { fixed: 0, invest: 0, savings: 0, gfs: 0 };
    // catTotal is take-home spending only; deductions are tracked separately
    // because they build assets without ever passing through the account.
    let catTotal = 0, deductionTotal = 0, assetTotal = 0, principal = 0, investContrib = 0;

    for (const cat of cats) {
      // start from item-level dials
      let value = 0;
      for (const item of cat.items) {
        let iv = item.base * ((ignoreDials ? item.defaultDialPct : dialOf(dials, item)) / 100);
        for (const e of events) {
          if (!e.enabled || e.type !== 'items' || !e.targets?.includes(item.id)) continue;
          if (m >= e.startMonth && (e.endMonth == null || m <= e.endMonth)) {
            iv = e.mode === 'abs' ? e.amount : item.base * (e.amount / 100);
          }
        }
        value += iv;
      }
      // a whole-category override wins over item dials
      for (const e of events) {
        if (!e.enabled || e.type !== 'category' || e.target !== cat.key) continue;
        if (m >= e.startMonth && (e.endMonth == null || m <= e.endMonth)) {
          value = e.mode === 'pct' ? cat.base * (e.amount / 100) : e.amount;
        }
      }
      if (cat.inflates) value *= inflationFactor;

      byCategory[cat.key] = value;
      if (cat.deduction === null) {
        catTotal += value;
        byBucket[cat.bucket] += value;
      } else {
        deductionTotal += value;
      }
      if (cat.asset) assetTotal += value;
      // Every invest-bucket line compounds, whichever group it lives in —
      // naming k401/roth/brokerage explicitly meant a new investment line
      // (an HSA, say) quietly stopped earning a return.
      if (cat.bucket === 'invest') investContrib += value;
      if (cat.principal && value > 0) principal += cat.principal;
    }

    /*
     * Dialing a payroll deduction moves take-home, not the budget.
     *
     * Notion's Net figure already has the base 401K and HSA contributions
     * removed, so the baseline needs no adjustment — but raising the 401K dial
     * to 200% means a smaller paycheck, not free assets. Charge only the delta
     * from base, which leaves every baseline number untouched and stops the
     * projection minting money whenever a deduction is dialed up.
     */
    const deductionBase = cats.reduce((s, c) => s + (c.deduction !== null ? c.base : 0), 0);
    // The pay that actually lands this month. Falls back to the flat twelfth
    // when no calendar is available — see `netByMonth`.
    const landed = netByMonth ? netByMonth(m) : netMonthlyIncome;
    let monthIncome = landed * Math.pow(1 + income.annualRaisePct / 100, yr)
      - (deductionTotal - deductionBase);
    let eventCost = 0, eventIncome = 0, oneOffCost = 0, oneOffIncome = 0;

    for (const e of events) {
      if (!e.enabled) continue;
      const on = m >= e.startMonth && (e.endMonth == null || m <= e.endMonth);
      if (e.type === 'cost' && on) eventCost += e.amount;
      if (e.type === 'income' && on) eventIncome += e.amount;
      if (e.type === 'oneoffCost' && m === e.startMonth) oneOffCost += e.amount;
      if (e.type === 'windfall' && m === e.startMonth) oneOffIncome += e.amount;
      if (e.type === 'incomeChange' && m >= e.startMonth) {
        // Measured against the nominal monthly figure: "drop to 60% of my
        // income" means 60% of what the job pays, not 60% of whichever month
        // this happens to be.
        const base = e.mode === 'pct' ? netMonthlyIncome * (e.amount / 100) : e.amount;
        const yearsSince = Math.max(0, yr - Math.floor(e.startMonth / 12));
        monthIncome = base * Math.pow(1 + income.annualRaisePct / 100, yearsSince);
      }
    }

    // annual bonus, grown by the same raise rate
    let bonus = 0;
    if (bGross > 0 && ((startMonth + m) % 12) === income.bonusMonth) {
      bonus = bGross * Math.pow(1 + income.annualRaisePct / 100, yr) * (income.bonusNetPct / 100);
    }

    byBucket.fixed += eventCost;

    const outflow = catTotal + eventCost + oneOffCost;
    const inflow = monthIncome + eventIncome + oneOffIncome + bonus;
    const surplus = inflow - outflow;

    cash += surplus;
    const buffer = ef0 + cash;
    if (buffer < 0 && brokeAt === null) brokeAt = m;

    const growth = investBalance * (marketReturnPct / 100 / 12);
    investBalance += investContrib + growth;
    netWorth += surplus + assetTotal + growth + principal;

    rows.push({
      m,
      label: monthLabel(startYear, startMonth, m),
      surplus, buffer, netWorth,
      income: monthIncome, eventIncome, bonus,
      // Ratio rather than a count, so a row knows it is a "big" month without
      // the projection having to carry the schedule list around.
      payRatio: netMonthlyIncome > 0 ? landed / netMonthlyIncome : 1,
      inflow, outflow, deductions: deductionTotal, byCategory, byBucket,
    });
  }

  // catValue(c, {}) rather than c.base: identical for an active-only category
  // list (every item defaults to 100%), but correct once a broader list can
  // carry a paused item whose untouched default is 0% — c.base alone would
  // count it as though it were already dialed on.
  const baseOut = cats.reduce((sum, c) => sum + (c.deduction === null ? catValue(c, {}) : 0), 0);
  return { rows, brokeAt, baseOut, baseSurplus: netMonthlyIncome - baseOut };
}

/**
 * What's moving the surplus at month `si`, biggest first.
 * Drives the waterfall chart and the plain-language diagnosis.
 */
export function buildImpacts(
  si: number,
  cats: DerivedCategory[],
  dials: Record<string, number>,
  events: ScenarioEvent[],
  income: IncomeAssumptions,
  netMonthlyIncome: number,
  grossMonthlyIncome: number,
): Impact[] {
  const out: Impact[] = [];

  for (const cat of cats) {
    if (cat.deduction !== null) continue; // never drawn from take-home
    // catValue(cat, {}) rather than cat.base — see runProjection's baseOut for
    // why: a paused item pulls cat.base up without counting toward the
    // untouched value, so cat.base alone would report a "dial" impact for a
    // category nobody has touched.
    const delta = catValue(cat, dials) - catValue(cat, {});
    if (Math.abs(delta) > 1) out.push({ label: `${cat.name} dial`, value: -delta });
  }

  for (const e of events) {
    if (!e.enabled) continue;
    const on = si >= e.startMonth && (e.endMonth == null || si <= e.endMonth);
    if (!on) continue;

    if (e.type === 'cost') out.push({ label: e.label, value: -e.amount });
    if (e.type === 'income') out.push({ label: e.label, value: e.amount });
    if (e.type === 'category') {
      const cat = cats.find(c => c.key === e.target);
      if (cat) {
        const next = e.mode === 'pct' ? cat.base * (e.amount / 100) : e.amount;
        out.push({ label: e.label, value: catValue(cat, dials) - next });
      }
    }
    if (e.type === 'items' && e.targets?.length) {
      let saved = 0;
      for (const cat of cats) {
        for (const item of cat.items) {
          if (!e.targets.includes(item.id)) continue;
          const current = item.base * (dialOf(dials, item) / 100);
          const next = e.mode === 'abs' ? e.amount : item.base * (e.amount / 100);
          saved += current - next;
        }
      }
      if (Math.abs(saved) > 1) out.push({ label: e.label, value: saved });
    }
    if (e.type === 'incomeChange') {
      const next = e.mode === 'pct' ? netMonthlyIncome * (e.amount / 100) : e.amount;
      out.push({ label: e.label, value: next - netMonthlyIncome });
    }
  }

  const yr = Math.floor(si / 12);
  if (income.annualRaisePct > 0 && yr > 0) {
    out.push({
      label: `Raises (${yr}yr)`,
      value: netMonthlyIncome * (Math.pow(1 + income.annualRaisePct / 100, yr) - 1),
    });
  }
  if (income.applyInflation && income.inflationPct > 0 && yr > 0) {
    const inflatable = cats.filter(c => c.inflates).reduce((s, c) => s + catValue(c, dials), 0);
    out.push({
      label: `Inflation (${yr}yr @ ${income.inflationPct}%)`,
      value: -inflatable * (Math.pow(1 + income.inflationPct / 100, yr) - 1),
    });
  }
  const bg = bonusGross(income, grossMonthlyIncome * 12);
  if (bg > 0) {
    out.push({ label: 'Bonus (spread monthly)', value: (bg * (income.bonusNetPct / 100)) / 12 });
  }

  return out.sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
}

/** Ordered list of cuts that would close a shortfall — powers the diagnosis text. */
export function suggestCuts(
  shortfall: number,
  cats: DerivedCategory[],
  dials: Record<string, number>,
  priority: PriorityEntry[],
  basis: NetBasis = 'accrual',
): { name: string; toPct: number; saves: number }[] {
  const out: { name: string; toPct: number; saves: number }[] = [];
  let need = shortfall;

  for (const key of cutOrder(priority)) {
    if (need <= 0) break;
    if (isLocked(key, priority)) continue;
    const cat = cats.find(c => c.key === key);
    if (!cat || cat.deduction !== null) continue;
    const current = catValue(cat, dials, basis);
    // Floor against active base, not cat.base — a paused item sitting in the
    // same group would otherwise inflate the floor and understate how much
    // room this category actually has to give up.
    const activeBase = cat.items.filter(i => i.status === 'A')
      .reduce((s, i) => s + baseOf(i, basis), 0);
    const floor = activeBase * (floorOf(cat, priority) / 100);
    const room = current - floor;
    if (room <= 1) continue;
    const take = Math.min(room, need);
    out.push({
      name: cat.name,
      toPct: ((current - take) / catBase(cat, basis)) * 100,
      saves: take,
    });
    need -= take;
  }
  return out;
}

/**
 * Default priority list for a fresh scenario.
 *
 * Pass the live CSP groups where they're available so a line added in Notion
 * gets a place in the cut order; without them this falls back to the keys the
 * ranking table knows, which is enough to read a scenario back out of D1.
 */
export function defaultPriority(groups: CspGroup[] = []): PriorityEntry[] {
  const order = groups.length ? cutPriorityFor(groups) : DEFAULT_CUT_ORDER;
  return order.map((groupKey, sortOrder) => ({
    groupKey,
    sortOrder,
    floorPct: GROUP_TUNING[groupKey]?.minPct ?? 0,
    locked: false,
  }));
}

export function defaultIncome(): IncomeAssumptions {
  return {
    annualRaisePct: 0,
    bonusMode: 'pct',
    bonusPct: 0,
    bonusAnnual: 0,
    bonusMonth: 2,
    bonusNetPct: 65,
    inflationPct: 3.5,
    applyInflation: true,
  };
}

export function emptyScenario(id: string, name: string): Scenario {
  return {
    id, name,
    isDefault: false,
    months: 36,
    marketReturnPct: 7,
    capAtIncome: false,
    income: defaultIncome(),
    dials: {},
    events: [],
    priority: defaultPriority(),
    items: [],
  };
}
